import { IPrintingSettings } from '@/interfaces/IPrintingSettings';
import { IBridgePrinter } from '@/interfaces/print-bridge-protocol';
import {
  DEFAULT_CODEPAGE,
  ILogicalPrinter,
  IPrintingV2Config,
  IUnresolvedV1Assignment,
  PRINTING_V2_SCHEMA_VERSION,
  getDefaultDestinationSettings,
} from './printing-v2.config';

/** Structural shape of the legacy `printing-settings` localStorage value. */
export interface IV1PersistedPrintingSettings {
  stickerPrinterName?: string | null;
  comandaPrinterNamesByStation?: Record<number, string | null>;
  ticketPrinterName?: string | null;
  stickerPrintingSettings?: IPrintingSettings;
  comandaPrintingSettings?: IPrintingSettings;
  ticketPrintingSettings?: IPrintingSettings;
}

interface IV1Reference {
  destination: 'ticket' | 'sticker' | 'comanda';
  stationId?: number;
  name: string;
}

/**
 * Pure v1 -> v2 migration (B3). Runs once per MACHINE, when hydration finds
 * no bridge document and no local cache.
 *
 * @returns the migrated config, or null when this browser held no v1 printer
 * assignments at all — in that case the caller must write NOTHING, so a
 * different browser holding real v1 data can still migrate later.
 */
export function migrateV1PrintingSettings({
  legacy,
  printers,
  now,
}: {
  legacy: IV1PersistedPrintingSettings | null;
  printers: IBridgePrinter[];
  now: number;
}): IPrintingV2Config | null {
  if (!legacy) return null;

  const references: IV1Reference[] = [];

  if (legacy.ticketPrinterName) {
    references.push({ destination: 'ticket', name: legacy.ticketPrinterName });
  }
  if (legacy.stickerPrinterName) {
    references.push({
      destination: 'sticker',
      name: legacy.stickerPrinterName,
    });
  }
  for (const key of Object.keys(legacy.comandaPrinterNamesByStation ?? {})) {
    const stationId = +key;
    const name = legacy.comandaPrinterNamesByStation?.[stationId];
    if (name) references.push({ destination: 'comanda', stationId, name });
  }

  if (!references.length) return null;

  // Codepage precedence when one v1 name is referenced by multiple
  // destinations with different encodings: ticket > comanda > sticker.
  const codepageForName = (name: string): string => {
    const referencedBy = references.filter((r) => r.name === name);
    for (const destination of ['ticket', 'comanda', 'sticker'] as const) {
      if (!referencedBy.some((r) => r.destination === destination)) continue;
      const encoding = legacy[`${destination}PrintingSettings`]?.encoding;
      if (encoding) return encoding;
    }
    return DEFAULT_CODEPAGE;
  };

  const logicalPrinters: ILogicalPrinter[] = [];
  const unresolvedV1: IUnresolvedV1Assignment[] = [];
  const logicalIdByName = new Map<string, number>();
  let nextId = 1;

  for (const name of new Set(references.map((r) => r.name))) {
    const printer = printers.find((p) => p.name === name);
    if (!printer) continue;

    const referencedBy = references.filter((r) => r.name === name);
    const stickerOnly = referencedBy.every((r) => r.destination === 'sticker');

    const logical: ILogicalPrinter = {
      id: nextId++,
      name,
      ref: printer.ref,
      transport: printer.transport,
      format: stickerOnly ? 'zpl' : 'escpos',
      paperWidthMm: 80,
      codepage: codepageForName(name),
    };
    logicalPrinters.push(logical);
    logicalIdByName.set(name, logical.id);
  }

  const assignments: IPrintingV2Config['assignments'] = {
    ticket: null,
    sticker: null,
    comandaByStation: {},
  };

  for (const reference of references) {
    const logicalId = logicalIdByName.get(reference.name) ?? null;

    if (logicalId === null) {
      unresolvedV1.push({
        destination: reference.destination,
        stationId: reference.stationId,
        v1Name: reference.name,
      });
    }

    switch (reference.destination) {
      case 'ticket':
        assignments.ticket = logicalId;
        break;
      case 'sticker':
        assignments.sticker = logicalId;
        break;
      case 'comanda':
        assignments.comandaByStation[reference.stationId!] = logicalId;
        break;
    }
  }

  return {
    schemaVersion: PRINTING_V2_SCHEMA_VERSION,
    savedAt: now,
    logicalPrinters,
    assignments,
    destinationSettings: {
      ticket: {
        ...getDefaultDestinationSettings(),
        ...(legacy.ticketPrintingSettings ?? {}),
      },
      comanda: {
        ...getDefaultDestinationSettings(),
        ...(legacy.comandaPrintingSettings ?? {}),
      },
      sticker: {
        ...getDefaultDestinationSettings(),
        ...(legacy.stickerPrintingSettings ?? {}),
      },
    },
    unresolvedV1,
    migratedFromV1: true,
  };
}
